/* eslint-disable @typescript-eslint/no-explicit-any */
import { Add, ArrowDownward, ArrowUpward, Delete } from "@mui/icons-material";
import { useMediaQuery, useTheme } from "@mui/material";
import { Component } from "@pimo/pimo-app-builder";
import React, { HTMLAttributes, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { Column } from "./column";
import { useEditorHighlightColor } from "./get-editor-highlight-color";
import { NewRowIcon } from "./new-row-icon";
import {
  DragDropLayoutComponentLayoutProps,
  DragDropLayoutRowDefinition,
} from "./types";

const defaultStyle: HTMLAttributes<HTMLDivElement>["style"] = {
  border: "rgba(0, 0, 0, 0) 2px solid",
};

const controlsHoverBridgePx = 38;
const controlsHorizontalOffsetPx = 4;

export const Row: React.FC<
  DragDropLayoutRowDefinition & {
    components: Component<
      any,
      any,
      any,
      any,
      DragDropLayoutComponentLayoutProps
    >[];
    rowId: number;
    isHovered: boolean;
    onHover: () => void;
    onLeave: () => void;
    isEditing: boolean;
    isComponentEditingEnabled?: boolean;
    onAddRowBelowClicked: (numberOfColumns: number) => void;
    onMoveRowUpClicked: (rowId: number) => void;
    onMoveRowDownClicked: (rowId: number) => void;
    onDeleteRowClicked: (rowId: number) => void;
    onComponentDeleted: (componentId: string) => void;
    onEditComponent: (componentId: string) => void;
    onEditRowClicked: (rowId: number) => void;
    onComponentDropped: (
      componentId: string,
      rowId: number,
      columnIndex: number
    ) => void;
    canDelete: boolean;
  }
> = ({
  rowId,
  columnCount,
  columns,
  stackBreakpoint = "md",
  components,
  onAddRowBelowClicked,
  isEditing,
  isComponentEditingEnabled,
  onComponentDropped,
  onComponentDeleted,
  onEditComponent,
  onDeleteRowClicked,
  isHovered,
  canDelete,
  onMoveRowDownClicked,
  onMoveRowUpClicked,
  onHover,
  onLeave,
}) => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down(stackBreakpoint));
  const editorHighlightColor = useEditorHighlightColor();

  const [newRowDialogAnchor, setNewRowDialogAnchor] =
    useState<HTMLElement | null>(null);
  const [rowRect, setRowRect] = useState<DOMRect | null>(null);
  const rowRef = React.useRef<HTMLDivElement>(null);
  const controlsRef = React.useRef<HTMLDivElement>(null);
  const addNewRowAnchorRef = React.useRef<HTMLDivElement>(null);

  const updateRowRect = () => {
    setRowRect(rowRef.current?.getBoundingClientRect() ?? null);
  };

  // Only track hover/rect outside edit mode's row controls are irrelevant, and
  // native `mouseover` bubbles from every nested descendant, so this must stay
  // gated to avoid a render loop while the mouse moves over row content.
  const handleHover = () => {
    if (!isEditing) return;
    updateRowRect();
    onHover();
  };

  const closeControls = () => {
    onLeave();
    setNewRowDialogAnchor(null);
  };

  const handleLeave = (event: React.MouseEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget;

    if (
      nextTarget instanceof Node &&
      controlsRef.current?.contains(nextTarget)
    ) {
      return;
    }

    closeControls();
  };

  useEffect(() => {
    if (!isHovered) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRowRect(null);
      return;
    }

    updateRowRect();
    window.addEventListener("scroll", updateRowRect, true);
    window.addEventListener("resize", updateRowRect);

    // The row's width also changes when sibling flex items (e.g. the
    // component library panel) are shown/hidden or animate via CSS
    // transitions, which fires neither a "scroll" nor a "resize" event.
    let resizeObserver: ResizeObserver | undefined;
    if (rowRef.current && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateRowRect);
      resizeObserver.observe(rowRef.current);
    }

    return () => {
      window.removeEventListener("scroll", updateRowRect, true);
      window.removeEventListener("resize", updateRowRect);
      resizeObserver?.disconnect();
    };
  }, [isHovered]);

  const style: HTMLAttributes<HTMLDivElement>["style"] = isHovered
    ? { border: `${editorHighlightColor} 2px solid` }
    : defaultStyle;

  const handleAddRowClick = (numberOfColumns: number) => {
    setNewRowDialogAnchor(null);
    onAddRowBelowClicked(numberOfColumns);
  };

  const controlsWidth = isNarrow ? 120 : 140;
  const controlsHeight = rowRect ? rowRect.height + 50 : 0;
  const toolbarLeft = 0;
  const toolbarTop = controlsHeight / 2 - 19;
  const addButtonTop = controlsHeight - 58;

  return (
    <>
      <div
        ref={rowRef}
        data-pimo-row
        style={{
          display: "flex",
          flexDirection: isNarrow ? "column" : "row",
          gap: isNarrow ? "12px" : "20px",
          alignItems: "stretch",
          padding: isNarrow ? "10px 12px" : "10px 20px",
          position: "relative",
          ...style,
        }}
        onMouseOver={handleHover}
        onMouseLeave={handleLeave}
      >
        {new Array(columnCount).fill(0).map((_, columnNumber) => {
          const column = columns.find((c) => c.columnIndex === columnNumber);
          const componentForColumn = components?.find(
            (component) =>
              component.layoutProps?.componentId === column?.componentId
          );
          return (
            <div
              key={columnNumber}
              style={{
                flex: isNarrow ? 1 : (column?.flex ?? 1),
                minWidth: 0,
                display: "flex",
              }}
            >
              <Column
                component={componentForColumn}
                onComponentDropped={(componentId) => {
                  onComponentDropped(componentId, rowId, columnNumber);
                }}
                onComponentDeleted={(componentId) => {
                  onComponentDeleted(componentId);
                }}
                onEditComponent={(componentId) => {
                  onEditComponent(componentId);
                }}
                isEditing={isEditing}
                isComponentEditingEnabled={isComponentEditingEnabled}
                canDelete={true}
              />
            </div>
          );
        })}
      </div>
      {isHovered &&
        rowRect &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            data-pimo-row-controls
            ref={controlsRef}
            onMouseEnter={updateRowRect}
            onMouseLeave={(event) => {
              const nextTarget = event.relatedTarget;

              if (
                nextTarget instanceof Node &&
                rowRef.current?.contains(nextTarget)
              ) {
                return;
              }

              closeControls();
            }}
            style={{
              position: "fixed",
              left: isNarrow
                ? rowRect.right - 120
                : rowRect.right + controlsHorizontalOffsetPx,
              top: rowRect.top,
              width: controlsWidth,
              height: controlsHeight,
              zIndex: theme.zIndex.drawer + 2,
              pointerEvents: "none",
            }}
          >
            <div
              data-pimo-row-controls-toolbar-bridge
              style={{
                position: "absolute",
                left: toolbarLeft - controlsHoverBridgePx,
                top: toolbarTop - controlsHoverBridgePx,
                width: controlsWidth + controlsHoverBridgePx * 2,
                height: 38 + controlsHoverBridgePx * 2,
                display: "flex",
                alignItems: "center",
                // Insets the pill by the bridge's own padding so its visible left edge lands at toolbarLeft.
                paddingLeft: controlsHoverBridgePx,
                pointerEvents: "auto",
              }}
            >
              <div
                style={{
                  background: editorHighlightColor,
                  height: "38px",
                  width: controlsWidth,
                  borderRadius: "100px",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-evenly",
                  alignItems: "center",
                  color: "#fff",
                }}
              >
                <div
                  style={{ cursor: "pointer", marginTop: "3px" }}
                  onClick={() => onMoveRowUpClicked?.(rowId)}
                >
                  <ArrowUpward />
                </div>
                <div
                  style={{ cursor: "pointer", marginTop: "3px" }}
                  onClick={() => onMoveRowDownClicked?.(rowId)}
                >
                  <ArrowDownward />
                </div>

                {canDelete && (
                  <>
                    <div
                      style={{
                        width: "1px",
                        height: "100%",
                        border: "1px solid #fff",
                      }}
                    ></div>
                    <div
                      style={{ cursor: "pointer", marginTop: "3px" }}
                      onClick={() => onDeleteRowClicked?.(rowId)}
                    >
                      <Delete />
                    </div>
                  </>
                )}
              </div>
            </div>
            <div
              data-pimo-row-controls-add-bridge
              style={{
                position: "absolute",
                // Same anchor as the toolbar bridge so the add button lines up with the pill.
                left: toolbarLeft - controlsHoverBridgePx,
                top: addButtonTop - controlsHoverBridgePx,
                width: 28 + controlsHoverBridgePx * 2,
                height: 28 + controlsHoverBridgePx * 2,
                pointerEvents: "auto",
              }}
            >
              <div
                ref={addNewRowAnchorRef}
                style={{
                  cursor: "pointer",
                  border: `${editorHighlightColor} 2px solid`,
                  position: "absolute",
                  left: controlsHoverBridgePx,
                  top: controlsHoverBridgePx,
                  height: "28px",
                  width: "28px",
                  borderRadius: "2px",
                }}
                onClick={(e) => {
                  e.preventDefault();
                  setNewRowDialogAnchor(addNewRowAnchorRef.current);
                }}
              >
                <Add style={{ color: editorHighlightColor }} />
              </div>

              {newRowDialogAnchor && (
                <div
                  data-pimo-row-controls-chooser
                  style={{
                    position: "absolute",
                    top: controlsHoverBridgePx + 58,
                    left: controlsHoverBridgePx + controlsWidth - 133,
                    border: "#d9d9d9 1px solid",
                    borderRadius: "12px",
                    height: "244px",
                    width: "188px",
                    display: "flex",
                    flexDirection: "column",
                    zIndex: 5,
                    background: "#fff",
                  }}
                >
                  <NewRowIcon
                    columns={4}
                    onClick={() => handleAddRowClick(4)}
                  />
                  <NewRowIcon
                    columns={3}
                    onClick={() => handleAddRowClick(3)}
                  />
                  <NewRowIcon
                    columns={2}
                    onClick={() => handleAddRowClick(2)}
                  />
                  <NewRowIcon
                    columns={1}
                    onClick={() => handleAddRowClick(1)}
                  />
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
